<template>
  <AppPageContainer :custom="1600" :min-height="0">
    <AppPageHeader design="new" title="Mailgun" compact />
    <AppTabs design="new" refined weight="medium" :tabs="[{ value: 'templates', label: 'Templates' }]" value="templates" />
    <main class="ui-page">
      <AppFilterToolbar>
        <template v-if="canEdit" #create><AppButton design="new" @click.native="openCreate">Create template</AppButton></template>
        <template #primary><AppInput :value="search" design="new" type="search" placeholder="Search by name, code or Mailgun ID" aria-label="Search Mailgun templates" @input="onSearch" /></template>
        <AppSelect :value="state" design="new" :options="states" :reduce="option => option.value" label="label" placeholder="Draft state" aria-label="Draft state" @input="setState" />
        <template v-if="search || state" #clear><AppClearFiltersButton design="new" icon-only @clear="clearFilters" /></template>
      </AppFilterToolbar>

      <AppContentSkeleton v-if="loading" label="Loading Mailgun templates" :columns="columns" min-width="1200px" />
      <AppErrorState v-else-if="error" :message="error" icon-only @retry="load" />
      <AppCard v-else-if="rows.length" design="new" variant="table" header-density="compact">
        <AppDataTable design="new" density="dense" :columns="columns" :rows="rows" clickable min-width="1200px" @row-click="openTemplate">
          <template #cell-name="{ row }"><AppTableText :text="row.name" /></template>
          <template #cell-code="{ row }"><AppTableText :text="row.code" /></template>
          <template #cell-draft_state="{ row }"><AppStatusBadge design="new" compact :tone="draftTone(row.draft_state)" :label="draftLabel(row.draft_state)" /></template>
          <template #cell-binding="{ row }">
            <span class="ui-cell-inline">
              <AppTableText :text="bindingId(row.binding)" fallback="No Mailgun binding" />
              <MailgunCopyButton v-if="bindingId(row.binding)" :value="bindingId(row.binding)" label="Mailgun ID" />
            </span>
          </template>
          <template #cell-active="{ row }"><AppStatusBadge design="new" compact :tone="row.active ? 'green' : 'neutral'" :label="row.active ? 'Active' : 'Inactive'" /></template>
          <template #cell-actions="{ row }"><AppIconButton :label="`Edit ${row.name}`" @keydown.enter.native.stop @keydown.space.native.stop @click="openEdit(row.id)"><i class="pi pi-pencil" aria-hidden="true" /></AppIconButton></template>
        </AppDataTable>
      </AppCard>
      <AppNoResults v-else design="new" title="No Mailgun templates found" text="" />
      <AppPagination v-if="!loading && !error" design="new" placement="embedded" :page="page" :count="count" :visible-count="rows.length" :page-size="40" :loading="loading" label="Mailgun template pages" @change="setPage" />
    </main>

    <TemplateModal v-if="selectedId" :key="selectedId" :template-id="selectedId" @close="closeTemplate" @saved="load" />
    <TemplateDetailsModal v-if="detailsOpen" :key="metadataKey" :template-id="metadataId" @close="closeMetadata" @saved="metadataSaved" />
  </AppPageContainer>
</template>

<script>
import { getSupportMailgunTemplates } from '@/http'
import AppTabs from '@/components/AppTabs'
import { techSupportKey } from '@/utils/techSupportAccess'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppIconButton from '@/components/AppIconButton.vue'
import AppTableText from '@/components/AppTableText.vue'
import MailgunCopyButton from './MailgunCopyButton.vue'
import TemplateDetailsModal from './TemplateDetailsModal.vue'
import TemplateModal from './TemplateModal.vue'

const COLUMNS = [
  { key: 'name', label: 'Name', width: '27%' },
  { key: 'code', label: 'Code', width: '17%' },
  { key: 'draft_state', label: 'Local draft', width: '14%' },
  { key: 'binding', label: 'Used for live sending', width: '27%' },
  { key: 'active', label: 'State', width: '10%' },
  { key: 'actions', label: '', width: '5%', align: 'center' }
]

export default {
  name: 'TechSupportMailgunTemplates',
  components: { AppTabs, AppContentSkeleton, AppErrorState, AppIconButton, AppTableText, MailgunCopyButton, TemplateDetailsModal, TemplateModal },
  data() {
    return {
      states: [
        { value: '', label: 'All drafts' },
        { value: 'ready', label: 'Ready' },
        { value: 'incomplete', label: 'Incomplete' },
        { value: 'empty', label: 'Empty' }
      ],
      search: '',
      rows: [],
      count: 0,
      loading: false,
      error: '',
      detailsOpen: false,
      metadataId: null,
      timer: null,
      requestNumber: 0
    }
  },
  computed: {
    canEdit() { return this.$store.getters['permissions/can'](techSupportKey('mailgun', 'edit')) },
    columns() { return this.canEdit ? COLUMNS : COLUMNS.filter(column => column.key !== 'actions') },
    selectedId() { return /^\d+$/.test(String(this.$route.query.template || '')) ? Number(this.$route.query.template) : null },
    metadataKey() { return this.metadataId ? `edit-${this.metadataId}` : 'create' },
    state() { return this.$route.query.state || '' },
    page() { return Math.max(1, Number(this.$route.query.page) || 1) },
    listKey() { return JSON.stringify([this.$route.query.search, this.state, this.page]) }
  },
  watch: {
    listKey: { immediate: true,
      handler() {
        this.search = this.$route.query.search || ''
        this.load()
      } }
  },
  beforeDestroy() { clearTimeout(this.timer); this.requestNumber += 1 },
  methods: {
    async load() {
      const request = ++this.requestNumber
      this.loading = true
      this.error = ''
      try {
        const { data } = await getSupportMailgunTemplates({ page: this.page, page_size: 40, search: this.$route.query.search || '', state: this.state })
        if (request === this.requestNumber) {
          this.rows = data.results || []
          this.count = data.count || 0
        }
      } catch (error) {
        if (request === this.requestNumber) this.error = error.response?.data?.details || 'Unable to load Mailgun templates.'
      } finally {
        if (request === this.requestNumber) this.loading = false
      }
    },
    onSearch(value) {
      this.search = value || ''
      clearTimeout(this.timer)
      this.timer = setTimeout(() => this.updateQuery({ search: this.search, page: null }), 350)
    },
    setState(value) { this.updateQuery({ state: value || null, page: null }) },
    setPage(page) { this.updateQuery({ page }) },
    clearFilters() { clearTimeout(this.timer); this.search = ''; this.updateQuery({ search: null, state: null, page: null }) },
    updateQuery(patch) {
      const query = { ...this.$route.query, ...patch }
      Object.keys(query).forEach(key => { if (query[key] === null || query[key] === '') delete query[key] })
      this.$router.push({ query }).catch(() => {})
    },
    openTemplate({ row }) { this.updateQuery({ template: row.id }) },
    closeTemplate() { this.updateQuery({ template: null }) },
    openCreate() { this.metadataId = null; this.detailsOpen = true },
    openEdit(id) { this.metadataId = id; this.detailsOpen = true },
    closeMetadata() { this.detailsOpen = false; this.metadataId = null },
    metadataSaved() { this.load() },
    bindingId(binding) { return (binding && (binding.remote_id || binding.remote_name)) || '' },
    draftTone(state) { return { ready: 'green', incomplete: 'amber', empty: 'neutral' }[state] || 'neutral' },
    draftLabel(state) { return { ready: 'Ready', incomplete: 'Incomplete', empty: 'No draft' }[state] || 'No draft' }
  }
}
</script>
